import { useState } from "react";
import { T, fontDisplay } from "../theme";
import { fmtDay } from "../engine/dates";
import { answerInvestorComment } from "../lib/data";

/**
 * Questions asked from the investor link.
 *
 * The link has a box for questions, so something has to be listening — an
 * unanswered question sitting in a table nobody opens is worse than not
 * offering the box at all. Answers appear back on the investor's own page.
 */
export default function QuestionsView({ comments = [], onChanged }) {
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  const unanswered = comments.filter((c) => !c.answer);
  const answered = comments.filter((c) => c.answer);

  const send = async (id) => {
    const text = (drafts[id] || "").trim();
    if (!text) return;
    setBusy(id);
    setError(null);
    try {
      await answerInvestorComment(id, text);
      setDrafts({ ...drafts, [id]: "" });
      await onChanged();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(null);
    }
  };

  const Card = ({ c }) => (
    <div
      style={{
        background: T.panel,
        borderRadius: 10,
        padding: 14,
        marginBottom: 10,
        border: c.answer ? undefined : `1px solid ${T.warn}`,
      }}
    >
      <div style={{ fontSize: 11, color: T.muted, marginBottom: 4 }}>
        {c.author_name || "An investor"} · {fmtDay(c.created_at)}
      </div>
      <div style={{ fontSize: 13, whiteSpace: "pre-wrap", marginBottom: 10 }}>{c.message}</div>

      {c.answer ? (
        <div
          style={{
            borderLeft: `2px solid ${T.income}`,
            paddingLeft: 10,
            fontSize: 12,
            whiteSpace: "pre-wrap",
            color: T.ink,
          }}
        >
          {c.answer}
          <div style={{ fontSize: 10, color: T.muted, marginTop: 4 }}>
            answered{c.answered_at ? ` ${fmtDay(c.answered_at)}` : ""}
          </div>
        </div>
      ) : (
        <>
          <textarea
            rows={2}
            value={drafts[c.id] || ""}
            onChange={(e) => setDrafts({ ...drafts, [c.id]: e.target.value })}
            placeholder="Your answer — this appears on their link."
            style={{
              width: "100%",
              fontFamily: "inherit",
              fontSize: 12,
              padding: 8,
              border: `1px solid ${T.border}`,
              borderRadius: 6,
              resize: "vertical",
            }}
          />
          <button
            onClick={() => send(c.id)}
            disabled={busy === c.id || !(drafts[c.id] || "").trim()}
            style={{
              marginTop: 6,
              padding: "5px 12px",
              fontSize: 11,
              borderRadius: 6,
              border: "none",
              background: (drafts[c.id] || "").trim() ? T.accent : T.border,
              color: (drafts[c.id] || "").trim() ? "#fff" : T.muted,
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            {busy === c.id ? "Sending…" : "Answer"}
          </button>
        </>
      )}
    </div>
  );

  return (
    <div style={{ maxWidth: 760 }}>
      <h2 style={{ fontFamily: fontDisplay, fontSize: 22, marginBottom: 4 }}>Questions</h2>
      <p style={{ fontSize: 12, color: T.muted, lineHeight: 1.6 }}>
        Asked from the investor link. Answers appear on their page under the question, so the thread
        stays in one place rather than in someone&rsquo;s inbox.
      </p>

      {error && <div style={{ color: T.cost, fontSize: 12, margin: "8px 0" }}>{error}</div>}

      {unanswered.length > 0 && (
        <div style={{ margin: "16px 0 8px", fontSize: 11, textTransform: "uppercase", letterSpacing: 0.8, color: T.warn }}>
          Waiting for an answer · {unanswered.length}
        </div>
      )}
      {unanswered.map((c) => (
        <Card key={c.id} c={c} />
      ))}

      {answered.length > 0 && (
        <div style={{ margin: "20px 0 8px", fontSize: 11, textTransform: "uppercase", letterSpacing: 0.8, color: T.muted }}>
          Answered
        </div>
      )}
      {answered.map((c) => (
        <Card key={c.id} c={c} />
      ))}

      {!comments.length && (
        <div style={{ fontSize: 12, color: T.muted, padding: 14, background: T.panel, borderRadius: 10, marginTop: 12 }}>
          No questions yet. Anyone with the investor link can ask one, and it lands here.
        </div>
      )}
    </div>
  );
}
